import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Select,
  Button,
  Card,
  CardBody,
  useToast,
  useDisclosure,
  Spinner,
  Icon,
  Badge,
  Alert,
  AlertIcon,
  SimpleGrid,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Divider,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as XLSX from "xlsx";
import {
  MdDownload,
  MdDescription,
  MdCalendarMonth,
  MdAccountBalance,
  MdRefresh,
  MdVisibility,
  MdAutoAwesome,
  MdContentCopy,
  MdCheckCircle,
  MdTableChart,
} from "react-icons/md";
import { useUser } from "../Context/userContext";
import { apiFetch } from "../lib/api";
import { supabase } from "../lib/supabase";
import { usePlaidLinkWithLogging } from "../hooks/usePlaidLinkWithLogging";
import { toUserMessage } from '../lib/apiError';

type AiState = "idle" | "streaming" | "done" | "error";

interface Statement {
  statement_id: string;
  month: number;
  year: number;
}

interface AccountStatements {
  account_id: string;
  account_name: string;
  statements: Statement[];
}

interface StatementsListResponse {
  accounts: AccountStatements[];
  request_id: string;
  error?: string;
  errorCode?: string;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const EnableStatementsBanner: React.FC<{ token: any; onEnabled: () => void }> = ({
  token,
  onEnabled,
}) => {
  const { selectedTenant } = useUser();
  const [loading, setLoading] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const toast = useToast();

  const handleEnable = async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const resp = await apiFetch(
        "/api/statements/link-token",
        {
          method: "POST",
          headers: { "x-tenant-id": selectedTenant.id, "Content-Type": "application/json" },
          body: JSON.stringify({ tokenId: token.id }),
        },
        session?.access_token,
      );
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      setLinkToken(data.link_token);
    } catch (err) {
      toast({
        title: "Error",
        description: toUserMessage(err, "We couldn't start the bank connection. Please try again."),
        status: "error",
      });
      setLoading(false);
    }
  };

  const { open, ready } = usePlaidLinkWithLogging({
    token: linkToken || "",
    mode: "reconnect",
    tenantId: selectedTenant?.id || "",
    onSuccess: () => {
      setLinkToken(null);
      setLoading(false);
      onEnabled();
    },
    onExit: () => {
      setLinkToken(null);
      setLoading(false);
    },
  });

  useEffect(() => {
    if (ready && linkToken) open();
  }, [ready, linkToken, open]);

  return (
    <Alert
      status="info"
      borderRadius="lg"
      mb={4}
      bg="bg.muted"
      borderWidth="1px"
      borderColor="xacto.mutedNavy"
    >
      <AlertIcon />
      <Box flex="1">
        <Text fontWeight="bold" color="text.primary">
          Enable Statements for {token?.institutionName || "Bank"}
        </Text>
        <Text fontSize="sm" color="text.secondary">
          We need your permission to access PDF statements for this bank.
        </Text>
      </Box>
      <Button colorScheme="cyan" size="sm" isLoading={loading} onClick={handleEnable}>
        Enable Statements
      </Button>
    </Alert>
  );
};

export const StatementsPage: React.FC = () => {
  const { selectedTenant, plaidTokens } = useUser();
  const toast = useToast();
  const { isOpen: isPreviewOpen, onOpen: openPreview, onClose: closePreview } = useDisclosure();

  const activeTokens = useMemo(
    () => plaidTokens.filter((t) => t.status === "ACTIVE"),
    [plaidTokens],
  );
  const isConnected = activeTokens.length > 0;

  const [selectedTokenId, setSelectedTokenId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [statementsData, setStatementsData] = useState<StatementsListResponse | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [notSupported, setNotSupported] = useState(false);

  const [aiState, setAiState] = useState<AiState>("idle");
  const [aiText, setAiText] = useState("");
  const [aiError, setAiError] = useState<string | null>(null);
  const [mathMatch, setMathMatch] = useState<boolean | null>(null);
  const [aiStructured, setAiStructured] = useState<Record<string, any> | null>(null);
  const [aiModels, setAiModels] = useState<string[]>([]);
  const [selectedAiModel, setSelectedAiModel] = useState<string>(
    "meta-llama/Meta-Llama-3.1-8B-Instruct",
  );
  const abortRef = useRef<AbortController | null>(null);

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  // Auto-select first active token
  useEffect(() => {
    if (activeTokens.length > 0 && !selectedTokenId) {
      setSelectedTokenId(activeTokens[0].id);
    }
  }, [activeTokens, selectedTokenId]);

  // Load available AI models once
  useEffect(() => {
    const load = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const resp = await apiFetch("/api/ai/models", { method: "GET" }, session.access_token);
        const data = await resp.json();
        if (Array.isArray(data.models) && data.models.length > 0) {
          setAiModels(data.models);
        }
      } catch {
        // non-critical — fallback to default model
      }
    };
    load();
  }, []);

  const fetchStatements = useCallback(async () => {
    if (!selectedTenant || !selectedTokenId) return;

    setLoading(true);
    setStatementsData(null);
    setNeedsConsent(false);
    setNotSupported(false);
    setSelectedAccountId("");
    setSelectedYear(null);
    setSelectedMonth(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");

      const response = await apiFetch(
        `/api/statements/list?tokenId=${selectedTokenId}`,
        {
          method: "GET",
          headers: { "x-tenant-id": selectedTenant.id },
        },
        session.access_token,
      );

      const data = await response.json();

      if (data.error) {
        const errMsg = String(data.error).toLowerCase();
        const errCode = data.errorCode || "";
        const isNotSupported =
          errMsg.includes("could not find statements") ||
          errCode === "PRODUCTS_NOT_SUPPORTED";
        const isNeedsConsent =
          !isNotSupported &&
          (errMsg.includes("consent") ||
            errMsg.includes("product") ||
            errCode === "ITEM_PRODUCT_NOT_READY");
        if (isNotSupported) {
          setNotSupported(true);
          return;
        }
        if (isNeedsConsent) {
          setNeedsConsent(true);
          return;
        }
        throw new Error(data.error);
      }

      setStatementsData(data);

      // Auto-select first account if available
      if (data.accounts && data.accounts.length > 0) {
        setSelectedAccountId(data.accounts[0].account_id);
      }
    } catch (error) {
      toast({
        title: "Error loading statements",
        description: toUserMessage(error, 'Please try again in a moment.'),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, selectedTokenId, toast]);

  // Fetch statements whenever the selected bank changes
  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  const availableAccounts = useMemo(() => statementsData?.accounts || [], [statementsData]);

  const accountStatements = useMemo(() => {
    if (!statementsData || !selectedAccountId) return [];
    const account = statementsData.accounts.find((a) => a.account_id === selectedAccountId);
    return account ? account.statements : [];
  }, [statementsData, selectedAccountId]);

  const totalStatementsCount = useMemo(() => accountStatements.length, [accountStatements]);

  const availableYears = useMemo(() => {
    const years = [...new Set(accountStatements.map((s) => s.year))];
    return years.sort((a, b) => b - a);
  }, [accountStatements]);

  const availableMonths = useMemo(() => {
    if (!selectedYear) return [];
    const months = accountStatements.filter((s) => s.year === selectedYear).map((s) => s.month);
    return [...new Set(months)].sort((a, b) => b - a);
  }, [accountStatements, selectedYear]);

  const selectedStatement = useMemo(() => {
    if (!selectedYear || !selectedMonth) return null;
    return accountStatements.find((s) => s.year === selectedYear && s.month === selectedMonth);
  }, [accountStatements, selectedYear, selectedMonth]);

  const handleTokenChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTokenId(e.target.value);
  };

  const handleAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAccountId(e.target.value);
    setSelectedYear(null);
    setSelectedMonth(null);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(e.target.value) || null);
    setSelectedMonth(null);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(parseInt(e.target.value) || null);
  };

  const fetchPdfBlob = useCallback(async (): Promise<Blob | null> => {
    if (!selectedStatement || !selectedTenant || !selectedTokenId) return null;

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Not authenticated");

    const response = await apiFetch(
      `/api/statements/${selectedStatement.statement_id}/download?tokenId=${selectedTokenId}`,
      {
        method: "GET",
        headers: { "x-tenant-id": selectedTenant.id },
      },
      session.access_token,
    );

    const data = await response.json();
    if (data.error) throw new Error(data.error);

    const binaryString = atob(data.pdf);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: "application/pdf" });
  }, [selectedStatement, selectedTenant, selectedTokenId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await fetchPdfBlob();
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement_${selectedYear}_${String(selectedMonth).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download complete",
        description: `Statement for ${MONTH_NAMES[(selectedMonth || 1) - 1]} ${selectedYear} downloaded.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: toUserMessage(error, 'Please try again in a moment.'),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const blob = await fetchPdfBlob();
      if (!blob) return;

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      openPreview();
    } catch (error) {
      toast({
        title: "Preview failed",
        description: toUserMessage(error, 'Please try again in a moment.'),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPreviewing(false);
    }
  };

  const handleClosePreview = () => {
    closePreview();
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleSummarize = async () => {
    if (!selectedStatement || !selectedTenant || !selectedTokenId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setAiState("streaming");
    setAiText("");
    setAiError(null);
    setMathMatch(null);
    setAiStructured(null);

    const selectedAccount = availableAccounts.find((a) => a.account_id === selectedAccountId);
    const bankName =
      activeTokens.find((t) => t.id === selectedTokenId)?.institutionName ?? "Unknown Bank";
    const params = new URLSearchParams({
      tokenId: selectedTokenId,
      accountId: selectedAccountId,
      year: String(selectedYear),
      month: String(selectedMonth),
      accountName: selectedAccount?.account_name ?? "",
      bankName,
      model: selectedAiModel,
    });

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");

      const resp = await apiFetch(
        `/api/statements/${selectedStatement.statement_id}/summarize?${params}`,
        {
          method: "POST",
          headers: { "x-tenant-id": selectedTenant.id, Accept: "text/event-stream" },
          signal: controller.signal,
        },
        session.access_token,
      );

      if (!resp.ok) {
        const errText = await resp.text().catch(() => `HTTP ${resp.status}`);
        throw new Error(errText);
      }

      const reader = resp.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          try {
            const chunk = JSON.parse(raw);
            if (chunk.type === "delta" && chunk.content) {
              setAiText((prev) => prev + chunk.content);
            } else if (chunk.type === "structured") {
              setAiStructured(chunk.data ?? null);
            } else if (chunk.type === "validation") {
              setMathMatch(chunk.match ?? false);
            } else if (chunk.type === "error") {
              setAiError(chunk.error ?? "Analysis error");
              setAiState("error");
              return;
            } else if (chunk.type === "done") {
              setAiState("done");
              return;
            }
          } catch {
            // malformed SSE line
          }
        }
      }
      setAiState("done");
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      setAiError(toUserMessage(err, 'Please try again in a moment.'));
      setAiState("error");
    }
  };

  const handleCopyAiText = () => {
    const clean = aiText
      .replace(/<<<JSON[\s\S]*?JSON>>>/g, "")
      .replace(/\nMATH_VALIDATION:.*$/m, "")
      .trim();
    navigator.clipboard.writeText(clean);
    toast({ title: "Copied to clipboard", status: "success", duration: 2000 });
  };

  const handleExportExcel = () => {
    const d = aiStructured ?? {};
    const bankName =
      activeTokens.find((t) => t.id === selectedTokenId)?.institutionName ?? "Unknown Bank";
    const monthLabel = MONTH_NAMES[(selectedMonth || 1) - 1];

    const fmt = (v: any) =>
      v === null || v === undefined || v === "<number or null>" || String(v).includes("<") ? "" : v;

    const rows = [
      ["Statement Summary Report"],
      [],
      ["Account Holder", fmt(d.accountHolder)],
      ["Bank", fmt(d.bank) || bankName],
      ["Account", fmt(d.accountName)],
      ["Account Type", fmt(d.accountType)],
      ["Period", fmt(d.period) || `${monthLabel} ${selectedYear}`],
      [],
      ["Financial Summary"],
      ["Previous Balance", fmt(d.previousBalance)],
      ["Ending Balance", fmt(d.endingBalance)],
      ["Total Credits (Income)", fmt(d.totalCredits)],
      ["Total Debits (Expenses)", fmt(d.totalDebits)],
      ["Net Cash Flow", fmt(d.netCashFlow)],
      ["Total Fees", fmt(d.totalFees)],
      ["Total Interest", fmt(d.totalInterest)],
      ["Minimum Payment Due", fmt(d.minimumPaymentDue)],
      ["Credit Limit", fmt(d.creditLimit)],
      ["Available Credit", fmt(d.availableCredit)],
      [],
      [
        "Math Validation",
        mathMatch === true ? "✓ Match" : mathMatch === false ? "✗ Mismatch" : "—",
      ],
      ["Generated", new Date().toLocaleString()],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 28 }, { wch: 30 }];

    // Style header rows
    ws["A1"] = { v: "Statement Summary Report", t: "s" };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Statement Summary");

    const fileName = `statement_${selectedYear}_${String(selectedMonth).padStart(2, "0")}_${(fmt(d.accountHolder) || "summary").replace(/\s+/g, "_")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleRefreshStatements = async () => {
    if (!selectedTenant || !selectedTokenId) return;

    setRefreshing(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not authenticated");

      const response = await apiFetch(
        `/api/statements/refresh?tokenId=${selectedTokenId}`,
        {
          method: "POST",
          headers: { "x-tenant-id": selectedTenant.id },
        },
        session.access_token,
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Statements refresh requested",
        description: `Plaid is fetching statements from ${data.start_date} to ${data.end_date}. Check again after the refresh completes.`,
        status: "success",
        duration: 7000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: toUserMessage(error, 'Please try again in a moment.'),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRefreshing(false);
    }
  };

  if (!isConnected) {
    return (
      <Box p={6}>
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Text>Please connect a bank account to access statements.</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack spacing={3}>
          <Icon as={MdDescription} boxSize={8} color="accent.primary" />
          <Box>
            <Heading size="lg" color="text.primary">
              Monthly Statements
            </Heading>
            <Text color="text.secondary">Download official bank statements in PDF format</Text>
          </Box>
          <Button
            leftIcon={<Icon as={MdRefresh} />}
            variant="outline"
            colorScheme="cyan"
            size="sm"
            ml="auto"
            onClick={handleRefreshStatements}
            isLoading={refreshing}
            loadingText="Refreshing..."
            isDisabled={!selectedTokenId}
          >
            Refresh 24 months
          </Button>
        </HStack>

        {notSupported && (
          <Alert
            status="warning"
            borderRadius="lg"
            mb={4}
            bg="bg.muted"
            borderWidth="1px"
            borderColor="xacto.mutedNavy"
          >
            <AlertIcon />
            <Box>
              <Text fontWeight="bold" color="text.primary">
                Statements not available for this institution
              </Text>
              <Text fontSize="sm" color="text.secondary">
                This bank does not support statement retrieval through Plaid.
              </Text>
            </Box>
          </Alert>
        )}

        {!notSupported && needsConsent && selectedTokenId && (
          <EnableStatementsBanner
            token={activeTokens.find((t) => t.id === selectedTokenId)}
            onEnabled={() => fetchStatements()}
          />
        )}

        {/* Bank selector (only shown when multiple banks connected) */}
        {activeTokens.length > 1 && (
          <Box>
            <HStack spacing={2} mb={2}>
              <Icon as={MdAccountBalance} color="accent.primary" />
              <Text fontWeight="medium" color="text.secondary" fontSize="sm">
                Bank
              </Text>
            </HStack>
            <Select
              value={selectedTokenId}
              onChange={handleTokenChange}
              bg="bg.muted"
              borderColor="xacto.mutedNavy"
              color="text.primary"
              _hover={{ borderColor: "accent.primary" }}
              size="md"
              maxW="360px"
            >
              {activeTokens.map((token) => (
                <option key={token.id} value={token.id}>
                  {token.institutionName ?? "Unknown Bank"}
                </option>
              ))}
            </Select>
          </Box>
        )}

        {/* Main Card */}
        <Card bg="bg.surface" borderWidth="1px" borderColor="xacto.mutedNavy">
          <CardBody>
            {loading ? (
              <VStack py={10}>
                <Spinner size="xl" color="accent.primary" />
                <Text color="text.secondary">Loading available statements...</Text>
              </VStack>
            ) : availableAccounts.length === 0 ? (
              <Alert
                status="info"
                borderRadius="lg"
                bg="transparent"
                border="1px"
                borderColor="blue.800"
              >
                <AlertIcon color="blue.400" />
                <Text>No statements available for this account.</Text>
              </Alert>
            ) : (
              <VStack spacing={6} align="stretch">
                <Badge colorScheme="cyan" alignSelf="flex-start" px={3} py={1} borderRadius="full">
                  {totalStatementsCount} statement{totalStatementsCount !== 1 ? "s" : ""} available
                </Badge>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <Box>
                    <Text mb={2} fontWeight="medium" color="text.secondary">
                      <Icon as={MdDescription} mr={2} />
                      Account
                    </Text>
                    <Select
                      placeholder="Select Account"
                      value={selectedAccountId}
                      onChange={handleAccountChange}
                      size="lg"
                      bg="bg.muted"
                      borderColor="xacto.mutedNavy"
                      color="text.primary"
                    >
                      {availableAccounts.map((acc) => (
                        <option key={acc.account_id} value={acc.account_id}>
                          {acc.account_name}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <Text mb={2} fontWeight="medium" color="text.secondary">
                      <Icon as={MdCalendarMonth} mr={2} />
                      Year
                    </Text>
                    <Select
                      placeholder="Choose a year"
                      value={selectedYear || ""}
                      onChange={handleYearChange}
                      isDisabled={!selectedAccountId}
                      size="lg"
                      bg="bg.muted"
                      borderColor="xacto.mutedNavy"
                      color="text.primary"
                    >
                      {availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Select>
                  </Box>

                  <Box>
                    <Text mb={2} fontWeight="medium" color="text.secondary">
                      <Icon as={MdCalendarMonth} mr={2} />
                      Month
                    </Text>
                    <Select
                      placeholder={selectedYear ? "Choose a month" : "Select year first"}
                      value={selectedMonth || ""}
                      onChange={handleMonthChange}
                      isDisabled={!selectedYear}
                      size="lg"
                      bg="bg.muted"
                      borderColor="xacto.mutedNavy"
                      color="text.primary"
                    >
                      {availableMonths.map((month) => (
                        <option key={month} value={month}>
                          {MONTH_NAMES[month - 1]}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </SimpleGrid>

                <HStack spacing={3} mt={4} flexWrap="wrap">
                  <Button
                    leftIcon={<Icon as={MdVisibility} />}
                    variant="outline"
                    colorScheme="cyan"
                    size="lg"
                    isDisabled={!selectedStatement}
                    isLoading={previewing}
                    loadingText="Loading..."
                    onClick={handlePreview}
                  >
                    Preview
                  </Button>

                  <Button
                    leftIcon={<Icon as={MdDownload} />}
                    colorScheme="cyan"
                    bg="xacto.electricCyan"
                    color="xacto.deepNavy"
                    size="lg"
                    isDisabled={!selectedStatement}
                    isLoading={downloading}
                    loadingText="Downloading..."
                    onClick={handleDownload}
                    _hover={{ bg: "cyan.400" }}
                  >
                    Download PDF
                  </Button>

                  <Button
                    leftIcon={<Icon as={MdAutoAwesome} />}
                    colorScheme="purple"
                    size="lg"
                    isDisabled={!selectedStatement}
                    isLoading={aiState === "streaming"}
                    loadingText="Analyzing..."
                    onClick={handleSummarize}
                  >
                    Summarize with AI
                  </Button>
                </HStack>

                {selectedStatement && (
                  <Text fontSize="sm" color="text.muted" textAlign="center">
                    {MONTH_NAMES[(selectedMonth || 1) - 1]} {selectedYear}
                  </Text>
                )}

                {/* AI Analysis Box */}
                {aiState !== "idle" && (
                  <Box
                    mt={4}
                    borderWidth="1px"
                    borderColor="purple.500"
                    borderRadius="lg"
                    bg="bg.muted"
                    overflow="hidden"
                  >
                    {/* Box header */}
                    <HStack
                      px={4}
                      py={3}
                      bg="purple.900"
                      justify="space-between"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <HStack spacing={2}>
                        <Icon as={MdAutoAwesome} color="purple.300" />
                        <Text fontWeight="bold" color="white">
                          Summary
                        </Text>
                        {aiState === "streaming" && <Spinner size="xs" color="purple.300" />}
                      </HStack>
                      <HStack spacing={2}>
                        {mathMatch !== null && aiState === "done" && (
                          <Badge
                            colorScheme={mathMatch ? "green" : "orange"}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            px={2}
                            py={1}
                            borderRadius="full"
                          >
                            <Icon as={MdCheckCircle} />
                            {mathMatch ? "Math Validated (100% Match)" : "Math Mismatch"}
                          </Badge>
                        )}
                        {aiState === "done" && aiText && (
                          <HStack spacing={2}>
                            <Button
                              leftIcon={<Icon as={MdTableChart} />}
                              size="sm"
                              variant="outline"
                              colorScheme="whiteAlpha"
                              color="white"
                              borderColor="whiteAlpha.400"
                              onClick={handleExportExcel}
                              isDisabled={!aiStructured}
                            >
                              Export summary to Excel
                            </Button>
                            <Button
                              leftIcon={<Icon as={MdContentCopy} />}
                              size="sm"
                              variant="outline"
                              colorScheme="whiteAlpha"
                              color="white"
                              borderColor="whiteAlpha.400"
                              onClick={handleCopyAiText}
                            >
                              Copy Text
                            </Button>
                          </HStack>
                        )}
                      </HStack>
                    </HStack>

                    <Divider borderColor="purple.700" />

                    {/* Content */}
                    <Box px={5} py={4} maxH="600px" overflowY="auto">
                      {aiState === "error" ? (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon />
                          <Text>{aiError}</Text>
                        </Alert>
                      ) : (
                        <Box
                          fontSize="sm"
                          color="text.primary"
                          sx={{
                            h2: {
                              fontSize: "md",
                              fontWeight: "bold",
                              mt: 4,
                              mb: 2,
                              color: "purple.300",
                            },
                            h3: { fontSize: "sm", fontWeight: "semibold", mt: 3, mb: 1 },
                            "ul, ol": { pl: 5, mb: 2 },
                            li: { mb: 1 },
                            p: { mb: 2, lineHeight: 1.7 },
                            table: {
                              width: "100%",
                              borderCollapse: "collapse",
                              mb: 3,
                              fontSize: "xs",
                            },
                            th: {
                              bg: "purple.900",
                              px: 2,
                              py: 1,
                              textAlign: "left",
                              borderWidth: "1px",
                              borderColor: "purple.700",
                            },
                            td: { px: 2, py: 1, borderWidth: "1px", borderColor: "whiteAlpha.200" },
                            strong: { color: "text.primary" },
                          }}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {aiText
                              .replace(/<<<JSON[\s\S]*?JSON>>>/g, "")
                              .replace(/\nMATH_VALIDATION:.*$/m, "")
                              .trim()}
                          </ReactMarkdown>
                          {aiState === "streaming" && (
                            <Text
                              as="span"
                              display="inline-block"
                              w="2px"
                              h="1em"
                              bg="purple.300"
                              ml={1}
                              animation="blink 1s step-end infinite"
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </VStack>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* PDF Preview Drawer */}
      <Drawer isOpen={isPreviewOpen} onClose={handleClosePreview} size="xl" placement="right">
        <DrawerOverlay />
        <DrawerContent bg="bg.surface">
          <DrawerCloseButton color="text.primary" />
          <DrawerHeader color="text.primary" borderBottomWidth="1px" borderColor="xacto.mutedNavy">
            {MONTH_NAMES[(selectedMonth || 1) - 1]} {selectedYear} — Statement
          </DrawerHeader>
          <DrawerBody p={0}>
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Statement Preview"
                width="100%"
                height="100%"
                style={{ border: "none", display: "block" }}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default StatementsPage;
